import { useCallback, useState } from "react";
import { Link } from "wouter";
import { Sun, Moon, BarChart3, Zap, CalendarCheck2, Trophy, Sparkles, Brain, Timer } from "lucide-react";
import { CHAPTER_GROUPS, TOTAL_COUNT } from "@/data/chapters";
import type { ChapterModule } from "@/data/chapters/types";
import { LogoLockup, LogoMark } from "@/components/Logo";
import { useTheme } from "@/components/ThemeProvider";
import {
  CommandSearch,
  SearchTriggerButton,
  useGlobalSearchHotkey,
} from "@/components/CommandSearch";
import { SyncStatus } from "@/components/SyncStatus";
import { UserBadge } from "@/components/UserBadge";
import { ChatPanel, AskAIHeaderButton } from "@/components/ChatPanel";
import { useAllProgress, summariseChapter, formatDuration, type ChapterProgress } from "@/lib/progress";
import { chipClass, toneFromPct } from "@/lib/status";

function StatBlock({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-md border border-card-border bg-card/60 px-4 py-3">
      <div className="text-[10.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      <div className="mt-1.5 text-xl sm:text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function ChapterCard({ chapter, progressAll }: { chapter: ChapterModule; progressAll: Record<string, ChapterProgress> }) {
  const { id, number, title } = chapter.meta;
  const flashTotal = (chapter.flashcardTerms && chapter.flashcardTerms.length > 0)
    ? chapter.flashcardTerms.length
    : chapter.glossary.length;
  const summary = summariseChapter(progressAll[id], chapter.examChecklist.length, flashTotal);
  const tone = toneFromPct(summary.scorePct ?? null);

  const meta: string[] = [];
  if (summary.timeSpentMs > 0) meta.push(formatDuration(summary.timeSpentMs));
  if (summary.attempted && summary.scorePct !== undefined) {
    meta.push(`${summary.scorePct}%`);
  } else if (summary.visited) {
    meta.push("Visited");
  }

  return (
    <Link href={`/chapter/${id}`} className="block touch-manipulation">
      <div
        className="group h-full rounded-lg border border-card-border bg-card p-4 sm:p-5 flex flex-col gap-3 transition-colors hover-elevate active:opacity-80 min-h-[120px]"
        data-testid={`chapter-card-${id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="font-mono text-hero tabular-nums text-foreground/80 leading-none">{number}</div>
          {summary.attempted && summary.scorePct !== undefined ? (
            <span
              className={`inline-flex items-center text-eyebrow tabular-nums px-1.5 py-0.5 rounded ${chipClass(tone)}`}
              data-testid={`card-score-${id}`}
            >
              {summary.scorePct}%
            </span>
          ) : summary.visited ? (
            <span
              className="text-eyebrow text-muted-foreground"
              data-testid={`card-visited-${id}`}
            >
              Visited
            </span>
          ) : null}
        </div>
        <h3 className="text-lead leading-snug">{title}</h3>
        <div className="mt-auto text-meta text-muted-foreground tabular-nums">
          {meta.length > 0 ? meta.join(" · ") : "Not started"}
        </div>
      </div>
    </Link>
  );
}

export default function Library() {
  const { theme, toggle } = useTheme();
  const [searchOpen, setSearchOpen] = useState(false);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  useGlobalSearchHotkey(openSearch);
  const [chatOpen, setChatOpen] = useState(false);
  const { all: progressAll, meta: progressMeta } = useAllProgress();
  const studiedCount = progressMeta.studiedChapters.length;

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header
        className="sticky top-0 z-40 backdrop-blur bg-background/90 border-b border-border h-14 flex items-center px-4 sm:px-6"
        data-testid="library-topbar"
      >
        <div className="flex-1 flex items-center gap-3 min-w-0">
          <LogoLockup />
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <AskAIHeaderButton onClick={() => setChatOpen(true)} />
          <SearchTriggerButton onClick={openSearch} />
          <SyncStatus />
          <Link
            href="/drill"
            className="p-2.5 rounded-md border border-card-border hover-elevate min-w-[40px] min-h-[40px] flex items-center justify-center"
            aria-label="Start quick drill"
            data-testid="link-drill"
          >
            <Zap className="w-4 h-4" />
          </Link>
          <Link
            href="/progress"
            className="p-2.5 rounded-md border border-card-border hover-elevate min-w-[40px] min-h-[40px] flex items-center justify-center"
            aria-label="Open progress dashboard"
            data-testid="link-progress"
          >
            <BarChart3 className="w-4 h-4" />
          </Link>
          <button
            onClick={toggle}
            className="p-2.5 rounded-md border border-card-border hover-elevate min-w-[40px] min-h-[40px] flex items-center justify-center"
            aria-label="Toggle theme"
            data-testid="theme-toggle"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <UserBadge />
        </div>
      </header>

      <div className="bg-dotgrid">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
          {/* Hero */}
          <section className="mb-8 sm:mb-10">
            <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground mb-3 flex items-center gap-2">
              <LogoMark size={18} />
              <span>Applied Behavior Analysis · Study Guide</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3 max-w-2xl">
              <StatBlock label="Chapters" value={String(TOTAL_COUNT)} sub="Hand-authored depth" />
              <StatBlock label="Sections" value="13" sub="Per chapter" />
              <StatBlock
                label="Studied"
                value={`${studiedCount}/${TOTAL_COUNT}`}
                sub={studiedCount > 0 ? "Visited at least once" : "Open any chapter to start"}
              />
            </div>
          </section>

          {/* Search + study modes */}
          <section className="mb-6 space-y-3">
            <div className="max-w-md">
              <SearchTriggerButton onClick={openSearch} variant="wide" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                href="/today"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] rounded-md border border-primary/30 bg-primary/10 text-primary hover-elevate text-[13.5px] sm:text-[13px] font-medium"
                data-testid="cta-today"
              >
                <CalendarCheck2 className="w-4 h-4" /> Today's review
              </Link>
              <Link
                href="/drill"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] rounded-md border border-card-border hover-elevate text-[13.5px] sm:text-[13px] font-medium"
                data-testid="cta-drill"
              >
                <Zap className="w-4 h-4" /> Quick Drill
              </Link>
              <Link
                href="/exam"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] rounded-md border border-card-border hover-elevate text-[13.5px] sm:text-[13px] font-medium"
                data-testid="cta-exam"
              >
                <Trophy className="w-4 h-4" /> Mock Exam
              </Link>
              <Link
                href="/coach"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] rounded-md border border-card-border hover-elevate text-[13.5px] sm:text-[13px] font-medium"
                data-testid="cta-coach"
              >
                <Sparkles className="w-4 h-4" /> AI Coach
              </Link>
              <Link
                href="/practice"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] rounded-md border border-card-border hover-elevate text-[13.5px] sm:text-[13px] font-medium"
                data-testid="cta-practice"
              >
                <Brain className="w-4 h-4" /> AI Practice
              </Link>
              <Link
                href="/focus"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] rounded-md border border-card-border hover-elevate text-[13.5px] sm:text-[13px] font-medium"
                data-testid="cta-focus"
              >
                <Timer className="w-4 h-4" /> Focus 25
              </Link>
            </div>
          </section>

          {/* Grouped chapter grid */}
          {CHAPTER_GROUPS.map(group => (
            <section key={group.part} className="mb-8 sm:mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  {group.part}
                </div>
                <div className="flex-1 h-px bg-border" />
                <div className="text-[11px] text-muted-foreground font-mono tabular-nums">
                  {group.items.length} ch
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {group.items.map(c => (
                  <ChapterCard key={c.meta.id} chapter={c} progressAll={progressAll} />
                ))}
              </div>
            </section>
          ))}

        </div>
      </div>
      <CommandSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <ChatPanel scope="library" open={chatOpen} onOpenChange={setChatOpen} hideFab />
    </div>
  );
}
